import { useMutation, useQuery } from 'react-query';

import { useSessionStore } from 'session/useSessionStore';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { axios } from 'configurations/axios';
import { GetQueueGroupsByUserParams, ISaveQueueGroupResult, IGetByUserResponse, IQueueGroupDetail, SaveQueueGroupParams } from '../models/interfaces/IQueueGroup';
import { IUploadInformation } from '../models/interfaces/IUploadInformation';

export const useSaveQueueGroup = () => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useMutation<ISaveQueueGroupResult, unknown, SaveQueueGroupParams>(
        async ({ token, name, queueType }) => {
            const response = await axios.post<IResponseAPI<ISaveQueueGroupResult>>(
                `${tenantConfig?.endpoints.queue}/Queue/QueueGroup/Save`,
                { Token: token, Name: name, QueueType: queueType },
                {
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        }
    );
};

export const useGetQueueGroupsByUser = ({ page, pageSize, search }: GetQueueGroupsByUserParams) => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useQuery<IGetByUserResponse>(
        ['QueueGroupsByUser', page, pageSize, search],
        async () => {
            const response = await axios.get<IResponseAPI<IGetByUserResponse>>(
                `${tenantConfig?.endpoints.queue}/Queue/QueueGroup/GetByUser`,
                {
                    params: { page, pageSize, search: search || undefined },
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        },
        {
            enabled: !!tenantConfig?.endpoints.queue,
            keepPreviousData: true,
        }
    );
};

export const useGetUploadInformation = () => {
    const { tenantConfig, jwt } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useQuery<IUploadInformation>(
        ['GetUploadInformation'],
        async () => {
            const response = await axios.get<IResponseAPI<IUploadInformation>>(
                `${tenantConfig?.endpoints.queue}/Queue/ItemQueue/GetInformationToUpload`,
                {
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        },
        {
            enabled: !!tenantConfig?.endpoints.queue,
        }
    );
};

export const useGetQueueGroupById = (token: string | null) => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useQuery<IQueueGroupDetail>(
        ['QueueGroupById', token],
        async () => {
            const response = await axios.get<IResponseAPI<IQueueGroupDetail>>(
                `${tenantConfig?.endpoints.queue}/Queue/QueueGroup/GetById`,
                {
                    params: { token },
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        },
        {
            enabled: !!token && !!tenantConfig?.endpoints.queue,
        }
    );
};
